import React from "react";
import { formatNumber2 } from "../../../utils/currencyFormat";
import { BieuDoDonut } from "components/GenericComponent/BieuDoDonut";
import { tinhTangTruong, dinhDangSo } from "./periodUtils";

const CAO_BIEU_DO = 300;
const DAY_VANH_DONUT = 50;
const DEM_BIEU_DO = 8;

// Hai màu này chỉ dùng để tô chữ / cung trên nền trắng nên lấy tông đậm hơn
// #00B69B / #FF5170 để không bị chìm.
const MAU_TANG = "#0E9384";
const MAU_GIAM = "#D92D4E";
const MAU_NEN = "#DFE1E7";
const MAU_CHU = "#2B335E"; // .color7
const MAU_PHU = "#5A6081";

const customStyles = `
  .tangtruong-body {
      display: flex;
      align-items: center;
      gap: 12px;
      flex: 1 1 auto;
      min-height: 0;
  }
  .tangtruong-body .tt-chart {
      flex: 1 1 58%;
      min-width: 0;
  }
  .tangtruong-body .tt-info {
      flex: 1 1 42%;
      min-width: 0;
  }
  .tangtruong-wrap .tt-ty-le {
      font-size: 28px;
      font-weight: 700;
      line-height: 1.15;
  }
  .tangtruong-wrap .tt-mo-ta {
      font-size: 12px;
      color: ${MAU_PHU};
  }
  .tangtruong-cg {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 6px 0;
  }
  .tangtruong-cg .tt-o {
      flex: 0 0 auto;
      width: 12px;
      height: 12px;
      border-radius: 3px;
      margin-top: 3px;
  }
  .tangtruong-cg .tt-ten {
      font-size: 13px;
      color: ${MAU_CHU};
  }
  .tangtruong-cg .tt-ngay {
      font-size: 11px;
      color: ${MAU_PHU};
  }
  .tangtruong-cg .tt-so {
      font-size: 13px;
      font-weight: 700;
      color: ${MAU_CHU};
  }
  .tangtruong-tong {
      padding-top: 10px;
      margin-top: 6px;
      border-top: 1px solid #eceef3;
  }
  .tangtruong-tong .tt-nhan {
      font-size: 13px;
      color: ${MAU_PHU};
  }
  .tangtruong-tong .tt-so {
      font-size: 20px;
      font-weight: 700;
  }
  @media (max-width: 1279px) {
      .tangtruong-body {
          flex-direction: column;
          align-items: stretch;
      }
      .tangtruong-body .tt-chart {
          flex: 0 0 auto;
          width: 100%;
      }
      .tangtruong-body .tt-info { flex: 0 0 auto; }
  }
`;

export const TangTruongLoiNhuan = ({
  kyNay,
  kyTruoc,
  loiNhuanKyNay,
  loiNhuanKyTruoc,
  loading,
}) => {
  const {
    kyNay: giaTriKyNay,
    kyTruoc: giaTriKyTruoc,
    chenhLech,
    tyLe,
  } = tinhTangTruong(loiNhuanKyNay, loiNhuanKyTruoc);

  const nhanKyNay = (kyNay && kyNay.nhan) || "Kỳ này";
  const nhanKyTruoc = (kyTruoc && kyTruoc.nhan) || "Kỳ trước";
  const ngayKyNay = (kyNay && kyNay.khoangNgay) || "";
  const ngayKyTruoc = (kyTruoc && kyTruoc.khoangNgay) || "";
  const coDuLieu = giaTriKyNay !== 0 || giaTriKyTruoc !== 0;

  // Kỳ trước lỗ (số âm) thì công thức chuẩn bị đảo dấu: -100 → 50 là cải thiện
  // nhưng cho ra -150%. Biểu đồ lấy mẫu số |kỳ trước| để hướng tăng/giảm luôn
  // khớp với dấu của chênh lệch; độ lớn vẫn đúng bằng tỷ lệ ở cột số liệu.
  let tyLeVe = null;
  if (giaTriKyTruoc !== 0) {
    tyLeVe = (chenhLech / Math.abs(giaTriKyTruoc)) * 100;
  } else if (chenhLech === 0) {
    tyLeVe = 0;
  }
  const veDuocBieuDo = coDuLieu && tyLeVe !== null;

  const tang = chenhLech > 0;
  const giam = chenhLech < 0;
  const bienDong = Math.abs(chenhLech);

  let trangThai = "tt-bang";
  if (tang) trangThai = "tt-tang";
  else if (giam) trangThai = "tt-giam";

  const mauBienDong = tang ? MAU_TANG : giam ? MAU_GIAM : MAU_CHU;

  // Vành xám luôn là trọn vẹn 100% của kỳ trước, phần biến động được đắp
  // thêm ra ngoài nên vành có tổng 100 + |tỷ lệ| (không cần kẹp giá trị).
  const ptNen = 100;
  const ptBienDong = tyLeVe === null ? 0 : Math.abs(tyLeVe);

  const tenNen = `Lợi nhuận ${nhanKyTruoc}`;
  const tenBienDong = giam
    ? `Giảm so với ${nhanKyTruoc}`
    : `Tăng thêm ${nhanKyNay}`;

  const chuTyLe =
    tyLeVe === null ? "N/A" : `${tyLeVe > 0 ? "+" : ""}${dinhDangSo(tyLeVe)}%`;
  const dauBienDong = tang ? "+" : giam ? "-" : "";
  const nhanBienDong = tang ? "Tăng thêm" : giam ? "Giảm" : "Không đổi";

  // c3 vẽ các cung theo thứ tự cột, xuất phát từ 12 giờ và chạy thuận chiều kim
  // đồng hồ. Vì vậy đặt cung màu lên đầu thì nó nằm bên PHẢI (tăng), đặt xuống
  // cuối thì nó khép về 12 giờ từ bên TRÁI (giảm).
  const cungNen = { ten: tenNen, giaTri: ptNen, mau: MAU_NEN };
  const cungBienDong = {
    ten: tenBienDong,
    giaTri: ptBienDong,
    mau: mauBienDong,
  };
  const duLieuBieuDo = giam
    ? [cungNen, cungBienDong]
    : [cungBienDong, cungNen];

  const renderChuGiai = (mau, ten, ngay, giaTri, phanTram) => (
    <div className="tangtruong-cg">
      <span className="tt-o" style={{ backgroundColor: mau }} />
      <div style={{ minWidth: 0 }}>
        <div className="tt-ten">{ten}</div>
        {ngay ? <div className="tt-ngay">{ngay}</div> : null}
        <div className="tt-so">
          {loading ? "..." : formatNumber2(giaTri)}
          {loading || phanTram === null ? "" : ` (${dinhDangSo(phanTram)}%)`}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`p-1 tangtruong-wrap ${trangThai}`}
      style={{
        minHeight: 400,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{customStyles}</style>
      <h4 className="interest-rate-title" style={{ flexShrink: 0 }}>
        Tăng trưởng lợi nhuận so với {nhanKyTruoc.toLowerCase()}
      </h4>

      <div className="tangtruong-body">
        <div className="tt-chart">
          <BieuDoDonut
            duLieu={duLieuBieuDo}
            coTheVe={veDuocBieuDo}
            chieuCao={CAO_BIEU_DO}
            dayVanh={DAY_VANH_DONUT}
            dem={DEM_BIEU_DO}
            loading={loading}
            // Chỉ ghi % của cung biến động, và ghi đúng giá trị của cung chứ
            // không phải tỷ lệ chiếm chỗ trên vành. Cung xám luôn là 100% nên
            // không cần nhãn.
            dinhDangNhan={(value, ratio, id) =>
              id === tenNen ? "" : `${dinhDangSo(value)}%`
            }
            dinhDangTooltip={(value, ratio, id) =>
              `${formatNumber2(
                id === tenNen ? giaTriKyTruoc : bienDong,
              )} (${dinhDangSo(value)}%)`
            }
            thongBaoTrong={
              !coDuLieu ? (
                "Không có dữ liệu lợi nhuận trong 2 kỳ"
              ) : (
                <span>
                  {nhanKyTruoc} không phát sinh lợi nhuận nên không tính được tỷ
                  lệ tăng trưởng
                </span>
              )
            }
            noiDungTam={
              <>
                <div className="tt-ty-le" style={{ color: mauBienDong }}>
                  {chuTyLe}
                </div>
                <div className="tt-mo-ta">
                  so với {nhanKyTruoc.toLowerCase()}
                </div>
              </>
            }
          />
        </div>

        <div className="tt-info">
          {renderChuGiai(
            MAU_NEN,
            tenNen,
            ngayKyTruoc,
            giaTriKyTruoc,
            veDuocBieuDo ? ptNen : null,
          )}
          {renderChuGiai(
            mauBienDong,
            tenBienDong,
            ngayKyNay,
            bienDong,
            veDuocBieuDo ? ptBienDong : null,
          )}
          <div className="tangtruong-tong">
            <div className="tt-nhan">{nhanBienDong}</div>
            <div className="tt-so" style={{ color: mauBienDong }}>
              {loading ? "..." : `${dauBienDong}${formatNumber2(bienDong)}`}
            </div>
          </div>
          {tyLe === null && !loading && (
            <div style={{ fontSize: 11, color: MAU_PHU, paddingTop: 6 }}>
              (*) {nhanKyTruoc} không phát sinh lợi nhuận nên không tính được tỷ
              lệ %.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
